import { NOTIFICATION_LEVELS, NOTIFICATION_LEVEL_LABELS, type NotificationEventDef, type NotificationLevel, type NotificationSettings } from '@clim/domain';
import { useEffect, useState } from 'react';
import { ErrorBox } from '../../components/ui';
import { useApi, useMutation } from './common';

interface Data {
  events: NotificationEventDef[];
  roles: { code: string; name: string }[];
  settings: NotificationSettings;
}

/**
 * Notifications configurables par type d'événement et par rôle (§15) : désactivée, dans l'application, ou push.
 * Une personne qui cumule plusieurs rôles reçoit le niveau le plus élevé. L'annonce urgente est toujours poussée.
 */
export function NotificationSettingsAdmin() {
  const res = useApi<Data>('/v1/admin/notification-settings');
  const m = useMutation();
  const [draft, setDraft] = useState<NotificationSettings>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (res.data) setDraft(res.data.settings);
  }, [res.data]);
  if (!res.data) return <ErrorBox message={res.error} />;
  const { events, roles } = res.data;
  const level = (e: NotificationEventDef, role: string): NotificationLevel => (e.locked ? e.defaultLevel : (draft[e.code]?.[role] ?? e.defaultLevel));
  const set = (e: NotificationEventDef, role: string, v: NotificationLevel) => {
    setSaved(false);
    setDraft({ ...draft, [e.code]: { ...(draft[e.code] ?? {}), [role]: v } });
  };
  const save = async () => {
    const out = (await m.run('PUT', '/v1/admin/notification-settings', draft)) as Data;
    setDraft(out.settings);
    setSaved(true);
  };
  const groups = [...new Set(events.map((e) => e.group))];
  return (
    <div>
      <div className="card">
        <div className="card-head">
          <h2>Notifications</h2>
          <button className="btn small primary" onClick={() => void save().catch(() => undefined)}>
            Enregistrer
          </button>
        </div>
        <p className="muted">
          Pour chaque événement et chaque rôle : désactivée, dans l’application (cloche, badge) ou push (téléphone). Une personne qui cumule plusieurs rôles
          reçoit le niveau le plus élevé. L’annonce urgente est toujours envoyée en push.
        </p>
        {saved && <p role="status">Réglages enregistrés.</p>}
        <ErrorBox message={m.error} />
        <table className="table compact" aria-label="Réglages des notifications">
          <thead>
            <tr>
              <th>Événement</th>
              {roles.map((r) => (
                <th key={r.code}>{r.name}</th>
              ))}
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g}>
              <tr>
                <th colSpan={roles.length + 1}>{g}</th>
              </tr>
              {events
                .filter((e) => e.group === g)
                .map((e) => (
                  <tr key={e.code}>
                    <td>{e.label}</td>
                    {roles.map((r) => (
                      <td key={r.code}>
                        <select
                          className="input"
                          value={level(e, r.code)}
                          disabled={e.locked}
                          aria-label={`${e.label} — ${r.name}`}
                          onChange={(ev) => set(e, r.code, ev.target.value as NotificationLevel)}
                        >
                          {NOTIFICATION_LEVELS.map((l) => (
                            <option key={l} value={l}>
                              {NOTIFICATION_LEVEL_LABELS[l]}
                            </option>
                          ))}
                        </select>
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
          ))}
        </table>
      </div>
    </div>
  );
}
